import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { findMinSignedExpiryMs } from 'helpers/signedUrl'

interface FollowingFeedCacheState {

  hasHydrated: boolean
  setHasHydrated: (v: boolean) => void

  feedCache: Record<string, any[]>
  feedUpdatedAt: Record<string, number> 
  scrollPos: Record<string, number> 

  prefetchWindowByKey: Record<string, string>
  setPrefetchWindow: (key: string, windowKey: string) => void
  getPrefetchWindow: (key: string) => string | undefined
  clearPrefetchWindow: (key: string) => void

  setFeed: (key: string, feed: any[]) => void
  getFeed: (key: string) => any[] | undefined

  setFeedUpdatedAt: (key: string, ts: number) => void
  getFeedUpdatedAt: (key: string) => number | undefined

  setScrollPos: (key: string, pos: number) => void
  getScrollPos: (key: string) => number | undefined

  removeFeed: (key: string) => void
  clear: () => void
}

export const useFollowingFeedCacheStore = create<FollowingFeedCacheState>()(
  persist(
    (set, get) => ({
      //  hydration flag
      hasHydrated: false,
      setHasHydrated: (v) => set({ hasHydrated: v }),

      feedCache: {},
      feedUpdatedAt: {},
      scrollPos: {},

      //  NEW (not persisted via partialize)
      prefetchWindowByKey: {},
      setPrefetchWindow: (key, windowKey) =>
        set((state) => ({
          prefetchWindowByKey: {
            ...state.prefetchWindowByKey,
            [key]: windowKey,
          },
        })),
      getPrefetchWindow: (key) => get().prefetchWindowByKey[key],
      clearPrefetchWindow: (key) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _removed, ...rest } = get().prefetchWindowByKey
        set({ prefetchWindowByKey: rest })
      },

      setFeed: (key, feed) =>
        set((state) => ({
          feedCache: { ...state.feedCache, [key]: feed },
          feedUpdatedAt: { ...state.feedUpdatedAt, [key]: Date.now() },
        })),

      getFeed: (key) => {
        const state = get()
        const feed = state.feedCache[key]

        if (!feed?.length) return undefined

        const updatedAt = state.feedUpdatedAt[key]

        // Reject legacy or excessively old cached feeds.
        if (
          !updatedAt ||
          Date.now() - updatedAt > 60 * 60 * 1000
        ) {
          return undefined
        }

        // Same signed-media expiry protection as PostsFeed.
        const expiresAt = findMinSignedExpiryMs(feed)

        if (
          expiresAt !== null &&
          expiresAt !== undefined &&
          Date.now() >= expiresAt - 10 * 60 * 1000
        ) {
          return undefined
        }

        return feed
      },

      setFeedUpdatedAt: (key, ts) =>
        set((state) => ({ feedUpdatedAt: { ...state.feedUpdatedAt, [key]: ts } })),

      getFeedUpdatedAt: (key) => get().feedUpdatedAt[key],

      setScrollPos: (key, pos) =>
        set((state) => ({ scrollPos: { ...state.scrollPos, [key]: pos } })),

      getScrollPos: (key) => get().scrollPos[key],

      removeFeed: (key) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _removed, ...newCache } = get().feedCache
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _tsRemoved, ...newTs } = get().feedUpdatedAt
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _posRemoved, ...newPos } = get().scrollPos
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _prefetchRemoved, ...newPrefetch } = get().prefetchWindowByKey

        set({
          feedCache: newCache,
          feedUpdatedAt: newTs,
          scrollPos: newPos,
          prefetchWindowByKey: newPrefetch,
        })
      },

      clear: () =>
        set({
          feedCache: {},
          feedUpdatedAt: {},
          scrollPos: {},
          prefetchWindowByKey: {},
        }),
    }),
    {
      name: 'following-feed-cache',

      //  mark hydrated once persist rehydrate completes
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },

      //  IMPORTANT: do NOT persist prefetchWindowByKey (session-only)
      partialize: (state) => ({
        feedCache: state.feedCache,
        feedUpdatedAt: state.feedUpdatedAt,
        scrollPos: state.scrollPos
      }),
    }
  )
)
